// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Save,
  Upload,
  Building,
  Globe,
  Clock,
  Trash2,
  AlertTriangle,
  Loader2,
  Info,
} from 'lucide-react';
import { useWorkspace, type Company } from '@/contexts/context';
import { supabase } from '@product-radar/shared';

// List of public email providers where domain-based workspace joining should be disabled
const PUBLIC_EMAIL_PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'protonmail.com',
  'proton.me',
  'aol.com',
  'mail.com',
  'live.com',
  'msn.com',
  'ymail.com',
  'rocketmail.com',
  'me.com',
  'mac.com',
  'zoho.com',
  'gmx.com',
  'fastmail.com',
];

const isPublicEmailProvider = (domain: string): boolean => {
  return PUBLIC_EMAIL_PROVIDERS.includes(domain.toLowerCase());
};

const SHOW_PERFORMANCE_SECTION = false;
const SHOW_WORKSPACE_STATUS = false;

export function GeneralSettings() {
  const { workspace, company, userRole, refreshWorkspace, refreshCompany } = useWorkspace();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFinalDeleteDialog, setShowFinalDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteWorkspaceDialog, setShowDeleteWorkspaceDialog] = useState(false);
  const [showDeleteWorkspaceConfirmDialog, setShowDeleteWorkspaceConfirmDialog] = useState(false);
  const [deleteWorkspaceConfirmation, setDeleteWorkspaceConfirmation] = useState('');
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Workspace fields
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceDomain, setWorkspaceDomain] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isPublicEmail, setIsPublicEmail] = useState(false);

  // Workspace profile / logo
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Company details (from companies table)
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('technology');
  const [companySize, setCompanySize] = useState('1-50');
  const [timezone, setTimezone] = useState('utc-8');
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // Localization / regional
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('usd');
  const [dateFormat, setDateFormat] = useState<'mdy' | 'dmy' | 'ymd' | 'iso'>('mdy');
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');

  // Track workspace ID to prevent resetting user changes on refresh
  const lastWorkspaceIdRef = useRef<string | null>(null);

  // Load workspace data and user email
  useEffect(() => {
    const loadData = async () => {
      if (workspace) {
        const isNewWorkspace = lastWorkspaceIdRef.current !== workspace.id;
        lastWorkspaceIdRef.current = workspace.id;

        // Always update these from workspace
        setWorkspaceName(workspace.name || '');
        setWorkspaceDomain(workspace.settings?.domain || '');
        setWorkspaceDescription(workspace.settings?.description || '');

        // Logo from settings or top-level column
        const existingLogo = workspace.settings?.logo_url || workspace.logo_url || null;
        setLogoUrl(existingLogo);

        // Localization & regional - use DB value if present, otherwise only set defaults on workspace change
        if (workspace.settings?.language) {
          setLanguage(workspace.settings.language);
        } else if (isNewWorkspace && typeof navigator !== 'undefined') {
          setLanguage((navigator.language || 'en').split('-')[0]);
        }

        if (workspace.settings?.currency) {
          setCurrency(workspace.settings.currency);
        } else if (isNewWorkspace) {
          setCurrency('usd');
        }

        if (workspace.settings?.date_format) {
          setDateFormat(workspace.settings.date_format);
        } else if (isNewWorkspace && typeof navigator !== 'undefined') {
          const locale = navigator.language || 'en-US';
          setDateFormat(locale === 'en-US' ? 'mdy' : 'dmy');
        }

        if (workspace.settings?.time_format) {
          setTimeFormat(workspace.settings.time_format);
        } else if (isNewWorkspace && typeof navigator !== 'undefined') {
          const locale = navigator.language || 'en-US';
          setTimeFormat(locale === 'en-US' ? '12h' : '24h');
        }
      }

      // Load company details from the company object
      if (company) {
        setCompanyName(company.name || '');
        if (company.industry) setIndustry(company.industry);
        if (company.company_size) {
          const legacy = company.company_size;
          const mapped = !legacy ? '1-50'
            : ['1-10', '11-50'].includes(legacy) ? '1-50'
            : legacy === '50-200' ? '51-150'
            : ['200-1000', '1000+'].includes(legacy) ? '151+'
            : legacy;
          setCompanySize(mapped);
        }
        if (company.timezone) {
          setTimezone(company.timezone);
        } else {
          const offsetMinutes = new Date().getTimezoneOffset();
          let derivedTz = 'utc+0';
          if (offsetMinutes === 480) derivedTz = 'utc-8';
          else if (offsetMinutes === 300) derivedTz = 'utc-5';
          else if (offsetMinutes === 0) derivedTz = 'utc+0';
          else if (offsetMinutes === -60) derivedTz = 'utc+1';
          else if (offsetMinutes === -540) derivedTz = 'utc+9';
          setTimezone(derivedTz);
        }
      }

      // Get current user's email to check if it's a public provider
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
        const domain = user.email.split('@')[1]?.toLowerCase() || '';
        setIsPublicEmail(isPublicEmailProvider(domain));
      }
    };

    loadData();
  }, [workspace, company]);

  const canEditWorkspace = userRole === 'admin';
  const isDomainDisabled = !canEditWorkspace || isPublicEmail;

  const handleSaveWorkspace = async () => {
    if (!workspace || !canEditWorkspace) return;

    setIsSaving(true);
    try {
      // Don't save domain if it's a public email provider
      const domainToSave = isPublicEmail ? '' : workspaceDomain;

      // Handle logo upload if a new file has been selected
      let newLogoUrl = logoUrl;
      if (logoFile) {
        const bucket = 'workspace-logos';
        const ext = logoFile.name.split('.').pop() || 'png';
        const path = `${workspace.id}/logo.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(path, logoFile, {
            upsert: true,
          });

        if (uploadError) {
          console.error('Error uploading logo:', uploadError);
          throw uploadError;
        }

        const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(path);

        newLogoUrl = publicUrlData?.publicUrl || null;
      }

      const { error } = await supabase
        .from('workspaces')
        .update({
          name: workspaceName,
          logo_url: newLogoUrl,
          settings: {
            ...workspace.settings,
            domain: domainToSave,
            description: workspaceDescription,
            language,
            currency,
            date_format: dateFormat,
            time_format: timeFormat,
          },
        })
        .eq('id', workspace.id);

      if (error) throw error;

      await refreshWorkspace();
      setLogoFile(null);
      setLogoPreviewUrl(null);
      toast.success('Workspace settings saved!', {
        description: 'Your workspace settings have been updated',
      });
    } catch (error: any) {
      console.error('Error saving workspace:', error);
      toast.error('Failed to save settings', {
        description: error.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!company || !canEditWorkspace) return;

    setIsSavingCompany(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({
          name: companyName,
          industry,
          company_size: companySize,
          timezone,
        })
        .eq('id', company.id);

      if (error) throw error;

      await refreshCompany();
      toast.success('Company details saved!', {
        description: 'Your company details have been updated',
      });
    } catch (error: any) {
      console.error('Error saving company:', error);
      toast.error('Failed to save company details', {
        description: error.message,
      });
    } finally {
      setIsSavingCompany(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1>General Settings</h1>
          <p className="text-muted-foreground">
            Manage your workspace preferences and organization details
          </p>
        </div>
        {/* Save buttons are now on each card */}
      </div>

      {/* Workspace Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Building className="h-5 w-5" />
            <CardTitle>Workspace Information</CardTitle>
          </div>
          <CardDescription>Basic information about your Product Radar workspace</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={logoPreviewUrl || logoUrl || undefined} />
                <AvatarFallback className="text-lg">PR</AvatarFallback>
              </Avatar>
              <Button
                size="sm"
                variant="outline"
                className="absolute -bottom-1 -right-1"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canEditWorkspace}
              >
                <Upload className="h-3 w-3" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setLogoFile(file);
                  const url = URL.createObjectURL(file);
                  setLogoPreviewUrl(url);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Workspace Logo</Label>
              <p className="text-sm text-muted-foreground">
                Upload a logo for your workspace (recommended: 200x200px, max 5MB)
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="workspaceName">Workspace Name</Label>
              <Input
                id="workspaceName"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="Enter workspace name"
                disabled={!canEditWorkspace}
              />
              {!canEditWorkspace && (
                <p className="text-xs text-muted-foreground">
                  Only workspace admins can edit this field
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspaceDomain">Workspace Domain</Label>
              <Input
                id="workspaceDomain"
                value={workspaceDomain}
                onChange={(e) => setWorkspaceDomain(e.target.value)}
                placeholder="example.com"
                disabled={isDomainDisabled}
              />
              {isPublicEmail ? (
                <Alert className="mt-2">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Domain-based workspace joining is disabled for public email providers (Gmail,
                    Yahoo, Outlook, etc.) for security reasons. Users must be invited to join your
                    workspace.
                  </AlertDescription>
                </Alert>
              ) : !canEditWorkspace ? (
                <p className="text-xs text-muted-foreground">
                  Only workspace admins can edit this field
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Users with this email domain can automatically join your workspace
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="workspaceDescription">Description</Label>
            <Textarea
              id="workspaceDescription"
              value={workspaceDescription}
              onChange={(e) => setWorkspaceDescription(e.target.value)}
              placeholder="Brief description of your workspace..."
              rows={3}
              disabled={!canEditWorkspace}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveWorkspace} disabled={!canEditWorkspace || isSaving} size="sm">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Workspace
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Company Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Building className="h-5 w-5" />
            <CardTitle>Company Details</CardTitle>
          </div>
          <CardDescription>
            Business information shared across all workspaces. Subscriptions are linked to this company.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                disabled={!canEditWorkspace}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry} disabled={!canEditWorkspace}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companySize">Company Size</Label>
              <Select
                value={companySize}
                onValueChange={setCompanySize}
                disabled={!canEditWorkspace}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-50">1-50 employees</SelectItem>
                  <SelectItem value="51-150">51-150 employees</SelectItem>
                  <SelectItem value="151+">151+ employees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone} disabled={!canEditWorkspace}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="utc-8">UTC-8 (PST)</SelectItem>
                  <SelectItem value="utc-5">UTC-5 (EST)</SelectItem>
                  <SelectItem value="utc+0">UTC+0 (GMT)</SelectItem>
                  <SelectItem value="utc+1">UTC+1 (CET)</SelectItem>
                  <SelectItem value="utc+9">UTC+9 (JST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveCompany} disabled={!canEditWorkspace || isSavingCompany || !company} size="sm">
              {isSavingCompany ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Company Details
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Localization & Regional */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Globe className="h-5 w-5" />
            <CardTitle>Localization & Regional</CardTitle>
          </div>
          <CardDescription>Language, currency, and regional preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="language">Display Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="ja">日本語</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Default Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usd">USD ($)</SelectItem>
                  <SelectItem value="eur">EUR (€)</SelectItem>
                  <SelectItem value="gbp">GBP (£)</SelectItem>
                  <SelectItem value="jpy">JPY (¥)</SelectItem>
                  <SelectItem value="cad">CAD (C$)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateFormat">Date Format</Label>
              <Select value={dateFormat} onValueChange={setDateFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                  <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                  <SelectItem value="ymd">YYYY-MM-DD</SelectItem>
                  <SelectItem value="iso">ISO 8601</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeFormat">Time Format</Label>
              <Select value={timeFormat} onValueChange={setTimeFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour (AM/PM)</SelectItem>
                  <SelectItem value="24h">24-hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance & Storage (currently disabled) */}
      {SHOW_PERFORMANCE_SECTION && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <CardTitle>Performance & Storage</CardTitle>
            </div>
            <CardDescription>Optimize performance and manage data retention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-save Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically save form changes without manual submission
                </p>
              </div>
              <Switch checked={autoSave} onCheckedChange={setAutoSave} />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Real-time Updates</Label>
                <p className="text-sm text-muted-foreground">Show live updates from team members</p>
              </div>
              <Switch defaultChecked />
            </div>

            <div className="space-y-2">
              <Label>Storage Usage</Label>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Files & Attachments</span>
                  <span>2.4 GB of 10 GB</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '24%' }}></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workspace Status (currently hidden) */}
      {SHOW_WORKSPACE_STATUS && (
        <Card>
          <CardHeader>
            <CardTitle>Workspace Status</CardTitle>
            <CardDescription>Current status and health of your workspace</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-green-600">Healthy</div>
                <div className="text-sm text-muted-foreground">System Status</div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold">99.9%</div>
                <div className="text-sm text-muted-foreground">Uptime (30 days)</div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold">45ms</div>
                <div className="text-sm text-muted-foreground">Avg Response Time</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Workspace */}
      <Card className="border-destructive/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Delete Workspace</CardTitle>
          </div>
          <CardDescription>
            Permanently delete this workspace and all its data. Your account will remain active and
            you can continue using other workspaces.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <h4 className="font-medium text-destructive mb-2">
                What happens when you delete this workspace:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• All feedback, ideas, clients, and workspace data will be permanently deleted</li>
                <li>• All workspace members will lose access</li>
                <li>• An email notification will be sent to all members</li>
                <li>• Your account is unchanged; you can still access other workspaces</li>
                <li>• This action cannot be reversed</li>
              </ul>
            </div>

            {!canEditWorkspace && (
              <p className="text-xs text-muted-foreground">
                Only workspace admins can delete this workspace.
              </p>
            )}
            <AlertDialog open={showDeleteWorkspaceDialog} onOpenChange={setShowDeleteWorkspaceDialog}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full" disabled={!canEditWorkspace}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete This Workspace
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete workspace?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the workspace &quot;{workspace?.name}&quot; and all its data.
                    All members will be notified. You will remain signed in and can access other workspaces.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => {
                      setShowDeleteWorkspaceDialog(false);
                      setShowDeleteWorkspaceConfirmDialog(true);
                    }}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
              open={showDeleteWorkspaceConfirmDialog}
              onOpenChange={(open) => {
                setShowDeleteWorkspaceConfirmDialog(open);
                if (!open) setDeleteWorkspaceConfirmation('');
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Final confirmation</AlertDialogTitle>
                  <AlertDialogDescription>
                    Type the workspace name <strong>{workspace?.name}</strong> below to confirm deletion.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Input
                    placeholder={workspace?.name || 'Workspace name'}
                    value={deleteWorkspaceConfirmation}
                    onChange={(e) => setDeleteWorkspaceConfirmation(e.target.value)}
                    className="w-full"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeleteWorkspaceConfirmation('')}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={
                      !workspace?.name ||
                      deleteWorkspaceConfirmation.trim() !== workspace.name.trim()
                    }
                    onClick={async () => {
                      if (!workspace) return;
                      if (deleteWorkspaceConfirmation.trim() !== workspace.name.trim()) return;

                      setIsDeletingWorkspace(true);
                      try {
                        const { error } = await supabase.functions.invoke('workspace-delete', {
                          body: { workspace_id: workspace.id },
                        });

                        if (error) {
                          toast.error('Failed to delete workspace', {
                            description: error.message || 'An unexpected error occurred',
                          });
                          return;
                        }

                        toast.success('Workspace deleted', {
                          description: 'All members have been notified.',
                        });
                        setShowDeleteWorkspaceConfirmDialog(false);
                        setDeleteWorkspaceConfirmation('');
                        window.location.href = '/';
                      } catch (err: any) {
                        toast.error('Failed to delete workspace', {
                          description: err.message || 'An unexpected error occurred',
                        });
                      } finally {
                        setIsDeletingWorkspace(false);
                      }
                    }}
                  >
                    {isDeletingWorkspace ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Permanently Delete Workspace'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Close Account */}
      <Card className="border-destructive/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Close Account</CardTitle>
          </div>
          <CardDescription>
            Permanently close your account and delete all associated data. This action cannot be
            undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <h4 className="font-medium text-destructive mb-2">
                What happens when you close your account:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• All feedback, ideas, and project data will be permanently deleted</li>
                <li>• Your workspace and all associated settings will be removed</li>
                <li>• Team members will lose access to shared data</li>
                <li>• All API keys and integrations will be revoked</li>
                <li>• This action cannot be reversed</li>
              </ul>
            </div>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Close My Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action will permanently delete your account, workspace, and all associated
                    data. Would you like to download your data before proceeding?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteDialog(false);
                      toast.success('Data export started!', {
                        description: "You'll receive an email when your data is ready for download",
                      });
                    }}
                  >
                    Download Data First
                  </Button>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setShowFinalDeleteDialog(true);
                    }}
                  >
                    Continue with Deletion
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showFinalDeleteDialog} onOpenChange={setShowFinalDeleteDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Final Confirmation</AlertDialogTitle>
                  <AlertDialogDescription>
                    To confirm deletion, please enter your full email address (<strong>
                      {userEmail}
                    </strong>
                    ) in the field below. This will permanently delete this workspace and all
                    associated data for every member. An email notification will be sent to all
                    workspace members.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Input
                    placeholder={userEmail || 'Enter your email to confirm'}
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    className="w-full"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeleteConfirmation('')}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={
                      !userEmail ||
                      deleteConfirmation.trim().toLowerCase() !== userEmail.trim().toLowerCase()
                    }
                    onClick={async () => {
                      if (!workspace) return;
                      if (
                        !userEmail ||
                        deleteConfirmation.trim().toLowerCase() !== userEmail.trim().toLowerCase()
                      ) {
                        return;
                      }

                      try {
                        const { error } = await supabase.functions.invoke('workspace-delete', {
                          body: { workspace_id: workspace.id },
                        });

                        if (error) {
                          console.error('Error deleting workspace:', error);
                          toast.error('Failed to delete workspace', {
                            description: error.message || 'An unexpected error occurred',
                          });
                          return;
                        }

                        toast.success('Workspace deletion initiated', {
                          description:
                            'The workspace has been scheduled for deletion and all members will be notified.',
                        });
                        setShowFinalDeleteDialog(false);
                        setDeleteConfirmation('');
                        // Redirect user out of the deleted workspace
                        window.location.href = '/';
                      } catch (err: any) {
                        console.error('Error deleting workspace:', err);
                        toast.error('Failed to delete workspace', {
                          description: err.message || 'An unexpected error occurred',
                        });
                      }
                    }}
                  >
                    Permanently Delete Workspace
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Individual save buttons are on each card above */}
    </div>
  );
}

